import { useEffect, useRef, type RefObject } from 'react';
import { focusFirstFocusable, getFocusableElements } from './focusTrap.js';

/** Focus trap + restore for modal overlays (Modal, Drawer). */
export function useOverlayFocusTrap(opened: boolean, panelRef: RefObject<HTMLElement | null>) {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!opened) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (panel) focusFirstFocusable(panel);
    });
    return () => {
      cancelAnimationFrame(raf);
      returnFocusRef.current?.focus?.();
      returnFocusRef.current = null;
    };
  }, [opened, panelRef]);

  useEffect(() => {
    if (!opened) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel?.contains(document.activeElement)) return;
      const focusables = getFocusableElements(panel);
      if (focusables.length < 2) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [opened, panelRef]);
}
